import React, { useState, useRef, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { cn } from '../lib/utils';

const { FiCalendar, FiChevronLeft, FiChevronRight } = FiIcons;

const DateFilter = ({ selectedDate, onDateChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    if (compareDate.getTime() === today.getTime()) {
      return 'Today';
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days in month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const isToday = (date) => {
    const today = new Date();
    return isSameDay(date, today);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handleDateSelect = (date) => {
    onDateChange(date);
    setIsOpen(false);
  };

  const days = getDaysInMonth(currentMonth);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-2 px-3 lg:px-4 h-11 bg-zinc-800 hover:bg-zinc-700 text-yellow-400 rounded-md transition-colors border border-zinc-700 shrink-0",
          isOpen && "bg-zinc-700"
        )}
        title="Filter by date"
      >
        <SafeIcon icon={FiCalendar} size={18} className="lg:w-5 lg:h-5" />
        <span className="font-bold text-xs lg:text-sm whitespace-nowrap">
          {formatDate(selectedDate)}
        </span>
      </button>

      {isOpen && (
        <div className="fixed lg:absolute inset-x-4 lg:inset-x-auto top-20 lg:top-full lg:right-0 lg:mt-2 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl z-50 p-3 lg:p-4 lg:min-w-[320px]">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-3 lg:mb-4">
            <button
              onClick={handlePrevMonth}
              className="p-2 hover:bg-zinc-800 rounded-md transition-colors text-zinc-400 hover:text-white"
            >
              <SafeIcon icon={FiChevronLeft} size={18} className="lg:w-5 lg:h-5" />
            </button>
            <h3 className="font-black text-white uppercase tracking-wide text-sm lg:text-base">
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h3>
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-zinc-800 rounded-md transition-colors text-zinc-400 hover:text-white"
            >
              <SafeIcon icon={FiChevronRight} size={18} className="lg:w-5 lg:h-5" />
            </button>
          </div>

          {/* Week Days */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map(day => (
              <div
                key={day}
                className="text-center text-[10px] lg:text-xs font-black text-zinc-500 uppercase py-1 lg:py-2"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((date, index) => {
              if (!date) {
                return <div key={`empty-${index}`} className="aspect-square" />;
              }

              const selected = isSameDay(date, selectedDate);
              const today = isToday(date);
              const isFuture = date > new Date();

              return (
                <button
                  key={date.toISOString()}
                  onClick={() => !isFuture && handleDateSelect(date)}
                  disabled={isFuture}
                  className={cn(
                    "aspect-square rounded-md text-xs lg:text-sm font-bold transition-all",
                    selected && "bg-yellow-400 text-black",
                    !selected && !isFuture && "hover:bg-zinc-800 text-white",
                    !selected && today && "ring-2 ring-yellow-400/50",
                    isFuture && "text-zinc-700 cursor-not-allowed"
                  )}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          {/* Quick Actions */}
          <div className="mt-3 lg:mt-4 pt-3 lg:pt-4 border-t border-zinc-800 flex gap-2">
            <button
              onClick={() => handleDateSelect(new Date())}
              className="flex-1 py-2 bg-zinc-800 hover:bg-zinc-700 text-yellow-400 rounded-md font-bold text-xs lg:text-sm transition-colors"
            >
              Today
            </button>
            <button
              onClick={() => {
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                handleDateSelect(yesterday);
              }}
              className="flex-1 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white rounded-md font-bold text-xs lg:text-sm transition-colors"
            >
              Yesterday
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateFilter;